import type { GccLocation, GccOwnership, GccSourcingTier } from './types'

// Zod-free so the UI can import it without pulling in the schema layer.

/**
 * Step 2 sourcing model (WI 2324): who owns the work (ownership) and where it is
 * delivered (location) are separate dimensions. MSP is an ownership option, never
 * a location.
 */
export const GCC_OWNERSHIPS: readonly GccOwnership[] = ['Captive-GCC', 'MSP', 'Retained']

export const GCC_LOCATIONS: readonly GccLocation[] = ['HQ', 'Markets', 'Nearshore', 'Offshore']

/**
 * Locations each ownership can deliver from. Retained work stays onshore, a captive
 * GCC sits nearshore or offshore, and an MSP can deliver from any location.
 */
export const GCC_VALID_LOCATIONS: Readonly<Record<GccOwnership, readonly GccLocation[]>> = {
  Retained: ['HQ', 'Markets'],
  'Captive-GCC': ['Nearshore', 'Offshore'],
  MSP: ['HQ', 'Markets', 'Nearshore', 'Offshore'],
}

/** Ownership × location for work that has no valid sourcing: it stays where it is assumed to be today. */
export const GCC_DEFAULT_SOURCING: Readonly<{ ownership: GccOwnership; location: GccLocation }> = {
  ownership: 'Retained',
  location: 'HQ',
}

/**
 * Automate first (WI 2048): a task whose AI disposition releases at least this
 * share of its baseline FTE is not migrated. Its residual work stays Retained.
 * Configurable per run in the wizard (whole percent, 1–100).
 */
export const GCC_DEFAULT_AUTOMATE_FIRST_THRESHOLD_PCT = 70

export const GCC_AUTOMATE_FIRST_LABEL = 'Automate first – do not migrate'

/** True for a whole percent from 1 to 100. */
export function isValidAutomateFirstThreshold(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 100
}

/**
 * True when a task is automate first: it has residual work left to source, and
 * the share AI releases ((baseline − residual) / baseline = 100 − residual %) is at
 * or above the threshold. Tasks with no residual work have nothing to migrate.
 */
export function isAutomateFirst(residualPct: number, residualFte: number, thresholdPct: number): boolean {
  return residualFte > 0 && 100 - residualPct >= thresholdPct
}

export function isGccOwnership(value: unknown): value is GccOwnership {
  return typeof value === 'string' && (GCC_OWNERSHIPS as readonly string[]).includes(value)
}

export function isGccLocation(value: unknown): value is GccLocation {
  return typeof value === 'string' && (GCC_LOCATIONS as readonly string[]).includes(value)
}

/** True when both values are known and the ownership can deliver from the location. */
export function isValidSourcingPair(ownership: unknown, location: unknown): boolean {
  return isGccOwnership(ownership) && isGccLocation(location) && GCC_VALID_LOCATIONS[ownership].includes(location)
}

/**
 * The 5-tier label (HQ / Markets / GCC-Offshore / GCC-Nearshore / MSP) for an
 * ownership × location pair. The eligibility rules, pay rates and Step 4 roles
 * still use the tier, so v2 designs derive it rather than store a second decision.
 */
export function toLegacyTier(ownership: GccOwnership, location: GccLocation): GccSourcingTier {
  if (ownership === 'MSP') return 'MSP'
  if (ownership === 'Captive-GCC') return location === 'Nearshore' ? 'GCC-Nearshore' : 'GCC-Offshore'
  return location === 'Markets' ? 'Markets' : 'HQ'
}

/** Display label, e.g. "Captive GCC · Offshore". */
export function formatSourcingPair(ownership: GccOwnership, location: GccLocation): string {
  return `${ownership === 'Captive-GCC' ? 'Captive GCC' : ownership} · ${location}`
}
